import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { useTranslation, Trans } from "react-i18next";
import { clearFormDraft, readFormDraft, writeFormDraft } from "@/lib/formDraftStorage";

interface SummaryStyle {
  careerFocus: string;
  experienceLevel: string;
  industryTone: string;
  personality: string;
}

interface PersonalInfoFormData {
  fullName: string;
  jobTitle: string;
  email: string;
  country: string;
  city: string;
  dialCode: string;
  phoneNumber: string;
  linkedinUrl?: string;
  portfolioUrl?: string;
  yearsExperience: number;
  summaryStyle: SummaryStyle;
  additionalInfo?: string;
}

interface PersonalInfoFormProps {
  onSubmit: (data: PersonalInfoFormData) => void;
}

interface CountryData {
  country: string;
  cities: string[];
}

interface DialCodeData {
  name: string;
  dial_code: string;
  code: string;
}

const FALLBACK_COUNTRIES: CountryData[] = [
  { country: "United States", cities: ["New York", "Los Angeles", "Chicago", "Houston", "San Francisco", "Other"] },
  { country: "United Kingdom", cities: ["London", "Manchester", "Birmingham", "Leeds", "Other"] },
  { country: "Canada", cities: ["Toronto", "Vancouver", "Montreal", "Calgary", "Other"] },
  { country: "Nigeria", cities: ["Lagos", "Abuja", "Port Harcourt", "Ibadan", "Other"] },
  { country: "Bangladesh", cities: ["Dhaka", "Chittagong", "Sylhet", "Khulna", "Other"] },
  { country: "India", cities: ["Mumbai", "Delhi", "Bengaluru", "Hyderabad", "Other"] },
  { country: "Australia", cities: ["Sydney", "Melbourne", "Brisbane", "Perth", "Other"] },
  { country: "Other", cities: ["Other"] },
];

const FALLBACK_DIAL_CODES: DialCodeData[] = [
  { name: "United States", dial_code: "+1", code: "US" },
  { name: "United Kingdom", dial_code: "+44", code: "GB" },
  { name: "Canada", dial_code: "+1", code: "CA" },
  { name: "Nigeria", dial_code: "+234", code: "NG" },
  { name: "Bangladesh", dial_code: "+880", code: "BD" },
  { name: "India", dial_code: "+91", code: "IN" },
  { name: "Australia", dial_code: "+61", code: "AU" },
  { name: "Other", dial_code: "+1", code: "OTHER" },
];

const defaultPersonalInfoFormData: PersonalInfoFormData = {
  fullName: "",
  jobTitle: "",
  email: "",
  country: "",
  city: "",
  dialCode: "+1",
  phoneNumber: "",
  linkedinUrl: "",
  portfolioUrl: "",
  yearsExperience: 0,
  summaryStyle: {
    careerFocus: "",
    experienceLevel: "",
    industryTone: "",
    personality: ""
  },
  additionalInfo: ""
};

const normalizePersonalInfoDraft = (draft: Partial<PersonalInfoFormData>): PersonalInfoFormData => ({
  ...defaultPersonalInfoFormData,
  ...draft,
  summaryStyle: {
    ...defaultPersonalInfoFormData.summaryStyle,
    ...(draft.summaryStyle || {})
  }
});

export function PersonalInfoForm({ onSubmit }: PersonalInfoFormProps) {
  const summaryStyleAlert = useTranslation().t('forms.personal.summaryStyleAlert');

  const careerFocusArray = JSON.parse(useTranslation().t('forms.personal.careerFocusArray'));

  const experienceLevelArray = JSON.parse(useTranslation().t('forms.personal.experienceLevelArray'));

  const industryToneArray = JSON.parse(useTranslation().t('forms.personal.industryToneArray'));

  const personalityArray = JSON.parse(useTranslation().t('forms.personal.personalityArray'));

  const labelsTrans = useTranslation().t('forms.personal.labels', { returnObjects: true });
  const placeholdersTrans = useTranslation().t('forms.personal.placeholders', { returnObjects: true });
  const buttonsTrans = useTranslation().t('forms.personal.buttons', { returnObjects: true });


  const [formData, setFormData] = useState<PersonalInfoFormData>(() => (
    normalizePersonalInfoDraft(readFormDraft('personalInfo', defaultPersonalInfoFormData))
  ));

  const [countries, setCountries] = useState<CountryData[]>(FALLBACK_COUNTRIES);
  const [dialCodes, setDialCodes] = useState<DialCodeData[]>(FALLBACK_DIAL_CODES);
  const [cities, setCities] = useState<string[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);

  useEffect(() => {
    // Fetch countries and cities
    fetch("https://countriesnow.space/api/v0.1/countries")
      .then(res => res.json())
      .then(response => {
        if (response.data) {
          setCountries(response.data);
        }
      })
      .catch(error => {
        console.error("Failed to fetch countries:", error);
        setCountries(FALLBACK_COUNTRIES);
      });

    // Fetch dial codes
    fetch("https://countriesnow.space/api/v0.1/countries/codes")
      .then(res => res.json())
      .then(response => {
        if (response.data) {
          setDialCodes(response.data);
        }
      })
      .catch(error => {
        console.error("Failed to fetch dial codes:", error);
        setDialCodes(FALLBACK_DIAL_CODES);
      });
  }, []);

  useEffect(() => {
    if (!formData.country || countries.length === 0) return;

    const selectedCountry = countries.find(c => c.country === formData.country);
    setCities(selectedCountry?.cities || []);
  }, [countries, formData.country]);

  useEffect(() => {
    writeFormDraft('personalInfo', formData);
  }, [formData]);

  const handleCountryChange = async (country: string) => {
    setFormData({ ...formData, country, city: "" });
    setCities([]);

    const selectedCountry = countries.find(c => c.country === country);
    if (selectedCountry) {
      setCities(selectedCountry.cities);
    }

    // Also update dial code for the selected country
    const countryDialCode = dialCodes.find(dc => dc.name === country);
    if (countryDialCode) {
      setFormData(prev => ({ ...prev, country, city: "", dialCode: countryDialCode.dial_code }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.summaryStyle.careerFocus || !formData.summaryStyle.experienceLevel ||
      !formData.summaryStyle.industryTone || !formData.summaryStyle.personality) {
      alert(summaryStyleAlert);
      return;
    }

    clearFormDraft('personalInfo');
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-6 bg-card rounded-lg border">
      <h2 className="text-2xl font-bold">{labelsTrans["personalInformation"]}</h2>

      <div className="space-y-2">
        <Label htmlFor="fullName">{labelsTrans["fullName"]}</Label>
        <Input
          id="fullName"
          required
          value={formData.fullName}
          onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
          placeholder={placeholdersTrans["John Doe"]}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="jobTitle">{labelsTrans["jobTitle"]}</Label>
        <Input
          id="jobTitle"
          required
          value={formData.jobTitle}
          onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
          placeholder={placeholdersTrans["jobTitle"]}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">{labelsTrans["email"]}</Label>
        <Input
          id="email"
          type="email"
          required
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="john@example.com"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="country">{labelsTrans["country"]}</Label>
        <Select required value={formData.country} onValueChange={handleCountryChange}>
          <SelectTrigger id="country">
            <SelectValue placeholder={placeholdersTrans["selectCountry"]} />
          </SelectTrigger>
          <SelectContent className="max-h-[300px] overflow-y-auto bg-background border z-50">
            {countries.map((countryData) => (
              <SelectItem key={countryData.country} value={countryData.country}>
                {countryData.country}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {formData.country && cities.length > 0 && (
        <div className="space-y-2">
          <Label htmlFor="city">{labelsTrans["city"]}</Label>
          <Select required value={formData.city} onValueChange={(value) => setFormData({ ...formData, city: value })}>
            <SelectTrigger id="city">
              <SelectValue placeholder={placeholdersTrans["selectCity"]} />
            </SelectTrigger>
            <SelectContent className="max-h-[300px] overflow-y-auto bg-background border z-50">
              {cities.map((city) => (
                <SelectItem key={city} value={city}>{city}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2 col-span-1">
          <Label htmlFor="dialCode">{labelsTrans["code"]}</Label>
          <Select value={formData.dialCode} onValueChange={(value) => setFormData({ ...formData, dialCode: value })}>
            <SelectTrigger id="dialCode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-[300px] overflow-y-auto bg-background border z-50">
              {dialCodes.map((dc) => (
                <SelectItem key={dc.code} value={dc.dial_code}>
                  {dc.dial_code} ({dc.name})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 col-span-2">
          <Label htmlFor="phone">{labelsTrans["phoneNumber"]}</Label>
          <Input
            id="phone"
            required
            value={formData.phoneNumber}
            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
            placeholder="1234567890"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="linkedin">{labelsTrans["linkedin"]}</Label>
        <Input
          id="linkedin"
          type="url"
          value={formData.linkedinUrl}
          onChange={(e) => setFormData({ ...formData, linkedinUrl: e.target.value })}
          placeholder="https://linkedin.com/in/username"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="portfolio">{labelsTrans["portfolio"]}</Label>
        <Input
          id="portfolio"
          type="url"
          value={formData.portfolioUrl}
          onChange={(e) => setFormData({ ...formData, portfolioUrl: e.target.value })}
          placeholder="https://yourportfolio.com"
        />
      </div>

      <div className="space-y-4">
        <Label htmlFor="yearsExperience">{labelsTrans["yearsExperience"]} {formData.yearsExperience}</Label>
        <Slider
          id="yearsExperience"
          value={[formData.yearsExperience]}
          onValueChange={(value) => setFormData({ ...formData, yearsExperience: value[0] })}
          max={50}
          step={1}
        />
      </div>

      <div className="space-y-4">
        <Label className="text-lg font-semibold">{labelsTrans["summaryStyle"]}</Label>

        <div className="space-y-3">
          <Label className="font-medium">{labelsTrans["careerFocus"]}</Label>
          <div className="space-y-2">
            {careerFocusArray.map((option) => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={`career-focus-${option}`}
                  checked={formData.summaryStyle.careerFocus === option}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setFormData({
                        ...formData,
                        summaryStyle: { ...formData.summaryStyle, careerFocus: option }
                      });
                    }
                  }}
                />
                <Label htmlFor={`career-focus-${option}`} className="font-normal cursor-pointer">{option}</Label>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <Label className="font-medium">{labelsTrans["experienceLevel"]}</Label>
          <div className="space-y-2">
            {experienceLevelArray.map((option) => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={`experience-level-${option}`}
                  checked={formData.summaryStyle.experienceLevel === option}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setFormData({
                        ...formData,
                        summaryStyle: { ...formData.summaryStyle, experienceLevel: option }
                      });
                    }
                  }}
                />
                <Label htmlFor={`experience-level-${option}`} className="font-normal cursor-pointer">{option}</Label>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <Label className="font-medium">{labelsTrans["industryTone"]}</Label>
          <div className="space-y-2">
            {industryToneArray.map((option) => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={`industry-tone-${option}`}
                  checked={formData.summaryStyle.industryTone === option}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setFormData({
                        ...formData,
                        summaryStyle: { ...formData.summaryStyle, industryTone: option }
                      });
                    }
                  }}
                />
                <Label htmlFor={`industry-tone-${option}`} className="font-normal cursor-pointer">{option}</Label>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <Label className="font-medium">{labelsTrans["personality"]}</Label>
          <div className="space-y-2">
            {personalityArray.map((option) => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={`personality-${option}`}
                  checked={formData.summaryStyle.personality === option}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setFormData({
                        ...formData,
                        summaryStyle: { ...formData.summaryStyle, personality: option }
                      });
                    }
                  }}
                />
                <Label htmlFor={`personality-${option}`} className="font-normal cursor-pointer">{option}</Label>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="additionalInfo">
          {labelsTrans["additionalInfo"]} <span className="italic text-muted-foreground">{labelsTrans["optional"]}</span>
        </Label>
        <Textarea
          id="additionalInfo"
          value={formData.additionalInfo}
          onChange={(e) => setFormData({ ...formData, additionalInfo: e.target.value })}
          placeholder={placeholdersTrans["additionalInfo"]}
          rows={4}
        />
      </div>

      <Button type="submit" className="w-full">{buttonsTrans["send"]}</Button>
    </form>
  );
}
